import { useContext } from "react";
import { ChatContext, ChatContextInterface } from "../components/MSRContext";
import Global from "../globals/globals";
import { useWebsocketHook } from "./useWebsocketHook";
import { mapJobRoleToMsrpRole, mapJobRoleToMsrpSubrole } from "../MSRP/utilities/msrpRole";
import { agentRoles } from "../constants/agent";

/**
 * The credential the MSRP digest handshake runs on. Either form works —
 * see MSRP/utilities/credential.js:
 *
 *   plaintext password  → form login, HA1 is computed client-side
 *   `h1_<md5hex>`       → SSO login, HA1 is provisioned by the gateway so the
 *                         user's password never enters the browser
 */
export interface SessionCredential {
  username: string;
  secret: string;
}

/** Shape of the agent payload returned by agentLogin / agentSsoLogin. */
export interface AgentSessionPayload {
  agent_role?: number;
  agent_displayname?: string;
  display_name?: string;
  company_domain: string;
  /** agent_role_assignment rows, in either the raw or joined form. */
  job_roles?: number[] | { role: number }[];
  [key: string]: unknown;
}

/**
 * Outcome of establishing a session.
 *
 *   "authenticated" → session is live, caller should navigate
 *   "reloading"     → the socket came back unusable and a page reload was
 *                     triggered; caller must not navigate
 */
export type EstablishSessionResult = "authenticated" | "reloading";

/**
 * Turns an authenticated agent payload + an MSRP credential into a live
 * session: opens the SCont socket, runs the digest handshake, and populates
 * the chat context plus the localStorage keys the reload path reads back
 * (see MSRContext's `authorizeWrapper`).
 *
 * Extracted from LoginPage so the form login and the SSO entry point share one
 * implementation — they differ only in how the credential is obtained.
 *
 * Throws when the socket cannot be created or the handshake is rejected; the
 * caller owns error presentation.
 */
export function useEstablishSession() {
  const { createSocketInstance } = useWebsocketHook();

  const {
    addRoom,
    handleReceiveFileMessage,
    handleReceiveMessage,
    requestController,
    handleSessionActivation,
    controller,
    setWrapper,
    handleChatListResponse,
    setIsCreator,
    setAvailableRooms,
    notificationController,
    addNotification,
    handleRemoveMessage,
    handleupdateMessage,
    handleaddRequest,
    handleremoveRequest,
    handleaddServerRequest,
    handleremoveServerRequest,
    handlefindServerRequest,
    handlefindRequest,
    handleupdateReqStatusById,
    handleAddMessageHistory,
    setIsAuthenticated,
    clearAllChatRooms,
    handleLeaveFromChat,
    handleAddParticipant,
    handleRemoveParticipant,
    updateMessageStatus,
  } = useContext(ChatContext) as ChatContextInterface;

  /**
   * Persist what the reload path needs to re-authorize without a login screen.
   * `password` holds whichever credential form was used — MSRContext feeds it
   * straight back into the wrapper, and msrpAuth decides how to treat it.
   */
  const saveLoginData = (
    username: string,
    secret: string,
    companyDomain: string,
    displayname?: string,
  ) => {
    Global.companyDomain = "@" + companyDomain;

    const loginData = {
      host: "127.0.0.1",
      port: 9915,
      username,
      password: secret,
      displayname: displayname || username,
      sessionId: `${Global.sessionId}`,
      serverIp: "127.0.0.1",
      serverPort: 3457,
      loginTimestamp: new Date().getTime(),
      companyDomain,
    };
    localStorage.setItem("wrapperData", JSON.stringify(loginData));

    Global.username = username;
    Global.displayname = displayname || username;
    Global.companyDomain = "@" + companyDomain;
  };

  const establishSession = async (
    agent: AgentSessionPayload,
    credential: SessionCredential,
  ): Promise<EstablishSessionResult> => {
    const { username, secret } = credential;
    const displayname = agent.agent_displayname || agent.display_name;

    localStorage.setItem("userData", JSON.stringify(agent));

    Global.companyDomain = "@" + agent.company_domain;
    Global.displayname = displayname || username;

    // Super admins have no SCont presence, so they skip the websocket auth.
    if (agent.agent_role === agentRoles.super_admin) {
      Global.username = username;
      localStorage.setItem("isAuthenticated", "true");
      localStorage.setItem("isAuthentication", "true");
      setIsAuthenticated(true);
      return "authenticated";
    }

    const msrpRole = mapJobRoleToMsrpRole();
    // No job role assigned maps to "backoffice", same as an empty list.
    const msrpSubrole = mapJobRoleToMsrpSubrole(agent.job_roles ?? []);
    console.log("Resolved MSRP role/subrole:", agent.job_roles, "→", msrpRole, msrpSubrole);

    const wrp = createSocketInstance(
      username,
      secret,
      agent.company_domain,
      msrpRole,
      msrpSubrole,
      addRoom,
      handleReceiveFileMessage,
      handleReceiveMessage,
      requestController,
      notificationController,
      controller,
      handleSessionActivation,
      handleChatListResponse,
      setIsCreator,
      setAvailableRooms,
      addNotification,
      handleRemoveMessage,
      handleupdateMessage,
      handleaddRequest,
      handleremoveRequest,
      handleaddServerRequest,
      handleremoveServerRequest,
      handlefindServerRequest,
      handlefindRequest,
      handleupdateReqStatusById,
      handleAddMessageHistory,
      clearAllChatRooms,
      handleLeaveFromChat,
      handleAddParticipant,
      handleRemoveParticipant,
      updateMessageStatus,
    );

    if (!wrp) {
      throw new Error("Failed to create socket instance.");
    }

    const socketStatus = await wrp.openSocket();

    if (!socketStatus || typeof socketStatus === "object") {
      location.reload();
      return "reloading";
    }

    const authResult = await wrp.authorize();

    if (authResult !== 1) {
      throw new Error("User authorization failed");
    }

    saveLoginData(username, secret, agent.company_domain, displayname);

    localStorage.setItem("wrapper", wrp.toString());
    localStorage.setItem("isAuthentication", "true");
    setWrapper(wrp);
    setIsAuthenticated(true);

    return "authenticated";
  };

  return { establishSession, setIsAuthenticated };
}
